import { useState } from "react";
import { Trans } from "@lingui/macro";

import PathwayBody from "@app/components/dashboard-pathway/PathwayBody";
import {
  useHiddenFieldKeys,
  useShownFieldKeys,
} from "@app/zustand/field-visibility";
import { createCohortPathwayStore, PathwayStoreProvider } from "@app/zustand/dashboard-pathway";
import { usePreviewView } from "@app/zustand/preview-view";
import useUser from "@app/zustand/user";
import { isInspectorRole } from "@app/utils/inspector-role";

type ScopedPathwayBoardProps =
  | {
      /** A catalogue cohort (PT-96). */
      cohortId: string;
    }
  | {
      /** A laboratory dataset: the dossiers it holds. */
      datasetId: string;
    };

/**
 * The exploration board over a population of its own: a catalogue cohort, replayed by the server in
 * its owner's context, or a laboratory dataset. Counted in episodes.
 *
 * Rendered under a store of its own, so a click inside cannot narrow the reader's dashboard and the
 * dashboard's selection cannot narrow this board. Whether its counter names the patients is the
 * server's answer (`names_patients`).
 */
export default function ScopedPathwayBoard(props: ScopedPathwayBoardProps) {
  // Created once per mount, and the host keys this component by population: reopening another one
  // starts from a fresh board rather than the previous one's clicks.
  const [store] = useState(createCohortPathwayStore);
  const user = useUser((state) => state.user);
  const previewRole = usePreviewView((state) => state.previewRole);
  const isGlobalView = isInspectorRole(
    previewRole ?? (user as { role?: string } | null)?.role,
  );
  const hiddenFieldKeys = useHiddenFieldKeys();
  const shownFieldKeys = useShownFieldKeys();

  return (
    <PathwayStoreProvider value={store}>
      <p className="mb-3 text-xs text-slate-500">
        {"datasetId" in props ? (
          <Trans>Dossiers de ce jeu de données, comptés dans le parcours de soins.</Trans>
        ) : (
          <Trans>
            Population de la cohorte telle que sa définition la sélectionne aujourd&apos;hui, comptée
            dans le parcours de soins.
          </Trans>
        )}
      </p>
      <PathwayBody
        cohortId={"cohortId" in props ? props.cohortId : undefined}
        datasetId={"datasetId" in props ? props.datasetId : undefined}
        isGlobalView={isGlobalView}
        hiddenFieldKeys={hiddenFieldKeys}
        shownFieldKeys={shownFieldKeys}
      />
    </PathwayStoreProvider>
  );
}
